import React, { useMemo } from "react";
import moment from "moment";
import momentTz from "moment-timezone";
import _get from "lodash/get";

import createGeneralInfoFields from "./createGeneralInfoFields";
import {
  FormPageSection,
  formSectionPropTypes,
} from "../../../layout/FormPageSection";
import { useV2DatacomponentResources } from "../../../hooks/useResources";
import { inputs } from "../../../inputs/inputConfigs";
import * as RequestTypes from "../../../../constants/RequestTypes";
import {
  closeModalDialog,
  openModalDialog,
} from "../../../../actions/layoutActions";
import useActions from "../../../hooks/useActions";
import { useUserRoleCan } from "../../../hooks/useUserRole";
import { usePrevious } from "../../../hooks/usePrevious";
import { getTimezoneFromClient } from "../../../../utils/timezoneUtils";

/**
 * Normalize a date to end-of-day in the specified timezone
 * Returns a Date object representing 23:59:59.999 in the timezone, stored as UTC
 */
const normalizeToEndOfDayInTimezone = (date, timezone) => {
  if (!timezone) {
    // eslint-disable-next-line no-console
    console.warn(
      "⚠️ No timezone provided to normalizeToEndOfDayInTimezone, defaulting to America/Denver"
    );
    timezone = "America/Denver";
  }

  const selectedDate = moment(date);

  const result = momentTz
    .tz(timezone)
    .year(selectedDate.year())
    .month(selectedDate.month())
    .date(selectedDate.date())
    .endOf("day")
    .toDate();

  // eslint-disable-next-line no-console
  console.log("📅 Client-side date normalization for closedAt:", {
    input: date,
    timezone,
    selectedDate: selectedDate.format("YYYY-MM-DD"),
    outputUTC: result.toISOString(),
    outputLocal: moment(result).format("YYYY-MM-DD HH:mm:ss"),
  });

  return result;
};

const getHandleStatusChange = ({
  closeModalDialog,
  openModalDialog,
  setFieldValue,
  closedAt,
  timezone,
}) => ({ target: { value } }) => {
  if (!["In Closing", "Closed"].includes(value)) {
    return;
  }

  if (value === "In Closing")
    openModalDialog(
      "Project Status - In Closing",
      "GeneralConfirmation",
      {
        title:
          "Please specify brand and flag before moving project into closing",
        sendButtonText: "Continue",
        onSubmit: closeModalDialog,
        hideCancelButton: true,
      },
      false,
      true
    );

  if (value === "Closed" && closedAt) {
    const formatedClosedAt = moment(closedAt).format("DD-MMM-yyyy");

    openModalDialog(
      "Project Status - Closed",
      "GeneralConfirmation",
      {
        title: `Would you like to update the Project Closing date to today from the current ${formatedClosedAt} date?`,
        sendButtonText: "Yes",
        cancelButtonText: "No",
        onSubmit: () => {
          // Calculate end-of-day timestamp in project's timezone
          const endOfDayTimestamp = normalizeToEndOfDayInTimezone(
            new Date(),
            timezone
          );
          setFieldValue("closedAt", endOfDayTimestamp);
          closeModalDialog();
        },
      },
      false,
      true
    );
  }
};

const GeneralInformationSection = props => {
  const brandId = _get(props.values, "brandId");
  const flagId = _get(props.values, "flagId");
  const propertyId = _get(props.values, "propertyId");

  const previousBrandId = usePrevious(brandId);
  const previousFlagId = usePrevious(flagId);

  if (previousBrandId && previousBrandId != brandId) {
    props.setFieldValue("flagId", null);
    props.setFieldValue("programId", null);
  }

  if (previousFlagId && previousFlagId != flagId) {
    props.setFieldValue("programId", null);
  }

  const actions = useActions({ openModalDialog, closeModalDialog });

  const properties = useV2DatacomponentResources(
    inputs.propertyId.dataComponentId,
    [],
    RequestTypes.LIST
  );

  const canCloseProjects = useUserRoleCan("close-projects");

  const selectedProperty = properties.find(
    property => property.id === propertyId
  );

  // Get timezone from client's scope for timestamp normalization
  const timezone = getTimezoneFromClient(props.client);

  const fields = useMemo(
    () =>
      createGeneralInfoFields({
        brandId,
        flagId,
        clientId: props.clientId,
        selectedProperty,
        isCancelled: props.values.isCancelled,
        cancelledBy: props.values.cancelledBy,
        setFieldValue: props.setFieldValue,
        handleStatusChange: getHandleStatusChange({
          closeModalDialog: actions.closeModalDialog,
          openModalDialog: actions.openModalDialog,
          setFieldValue: props.setFieldValue,
          closedAt: props.values.closedAt,
          timezone,
        }),
        canCloseProjects,
        status: props.values.status,
      }),
    [
      actions,
      brandId,
      canCloseProjects,
      flagId,
      props.clientId,
      props.setFieldValue,
      props.values.cancelledBy,
      props.values.closedAt,
      props.values.isCancelled,
      props.values.status,
      selectedProperty,
      timezone,
    ]
  );

  return <FormPageSection {...props} fields={fields} headerText="General" />;
};
GeneralInformationSection.propTypes = formSectionPropTypes;

export default GeneralInformationSection;
